import { z, ZodTypeAny } from 'zod'

import {
  evaluateAllConditions,
  evaluateAnyConditionGroup,
  evaluateSingleCondition
} from './evaluateCondition'
import type { FlowFieldConfig, FlowStepConfig } from './types'
import i18n from '@/src/i18n/config'

function getFieldLabel(field: FlowFieldConfig): string {
  const fallbackLabel =
    typeof field.label === 'string' && i18n.exists(field.label) ? i18n.t(field.label) : field.label
  return i18n.t(`fields.${field.key}.label`, { defaultValue: fallbackLabel })
}

function getValidationMessage(
  key: string,
  values?: Record<string, unknown>,
  defaultValue?: string
): string {
  return i18n.t(key, { ...values, defaultValue })
}

function isFieldVisible(field: FlowFieldConfig, allValues: Record<string, unknown>): boolean {
  const singleConditionOk = evaluateSingleCondition(field.visibleWhen, allValues)
  const allConditionsOk = evaluateAllConditions(field.visibleWhenAll, allValues)
  const anyGroupOk = evaluateAnyConditionGroup(field.visibleWhenAny, allValues)

  return singleConditionOk && allConditionsOk && anyGroupOk
}

/**
 * Build a field-level Zod schema from a single FlowFieldConfig.
 */
function buildFieldSchema(field: FlowFieldConfig): ZodTypeAny {
  const v = field.validation
  const fieldLabel = getFieldLabel(field)
  const hasVisibilityConditions =
    !!field.visibleWhen || !!field.visibleWhenAll?.length || !!field.visibleWhenAny?.length
  const applyRequiredInBaseSchema = !!field.required && !hasVisibilityConditions

  switch (field.type) {
    case 'checkbox': {
      if (v?.mustBeTrue && applyRequiredInBaseSchema) {
        return z.boolean().refine((val) => val === true, {
          message: getValidationMessage(
            'validation.mustBeTrue',
            { field: fieldLabel },
            `${fieldLabel} è obbligatorio`
          )
        })
      }
      return z.boolean().default(false)
    }

    case 'number': {
      let schema = z.coerce.number({
        message: getValidationMessage(
          'validation.invalidNumber',
          { field: fieldLabel },
          `${fieldLabel} deve essere un numero`
        )
      })
      if (v?.min !== undefined) {
        schema = schema.min(
          v.min,
          getValidationMessage('validation.min', { value: v.min }, `Minimo ${v.min}`)
        )
      }
      if (v?.max !== undefined) {
        schema = schema.max(
          v.max,
          getValidationMessage('validation.max', { value: v.max }, `Massimo ${v.max}`)
        )
      }
      if (!applyRequiredInBaseSchema) return schema.optional()
      return schema
    }

    case 'date': {
      let schema = z.string()
      if (applyRequiredInBaseSchema) {
        schema = schema.min(
          1,
          getValidationMessage(
            'validation.required',
            { field: fieldLabel },
            `${fieldLabel} è obbligatorio`
          )
        )
      }
      // minAge validation is handled at form level rather than Zod
      if (!applyRequiredInBaseSchema) return schema.optional().or(z.literal(''))
      return schema
    }

    case 'email': {
      const schema = z
        .string()
        .email(
          getValidationMessage(
            'validation.invalidEmail',
            undefined,
            'Inserisci un indirizzo email valido'
          )
        )
      if (!applyRequiredInBaseSchema) return schema.optional().or(z.literal(''))
      return schema
    }

    case 'textarea': {
      let schema = z.string()
      if (v?.maxLength) {
        schema = schema.max(
          v.maxLength,
          getValidationMessage(
            'validation.maxLength',
            { count: v.maxLength },
            `Massimo ${v.maxLength} caratteri`
          )
        )
      }
      if (applyRequiredInBaseSchema) {
        schema = schema.min(
          1,
          getValidationMessage(
            'validation.required',
            { field: fieldLabel },
            `${fieldLabel} è obbligatorio`
          )
        )
      }
      if (!applyRequiredInBaseSchema) return schema.optional().or(z.literal(''))
      return schema
    }

    case 'select':
    case 'radio':
    case 'city-grid': {
      // When options are known at config time, we validate as string
      const schema = z.string()
      if (applyRequiredInBaseSchema) {
        return schema.min(
          1,
          getValidationMessage(
            'validation.selectRequired',
            { field: fieldLabel.toLowerCase() },
            `Seleziona ${fieldLabel.toLowerCase()}`
          )
        )
      }
      return schema.optional().or(z.literal(''))
    }

    case 'date-picker-airbnb': {
      const requiredMessage = getValidationMessage(
        'validation.required',
        { field: fieldLabel },
        `${fieldLabel} è obbligatorio`
      )

      // Normalize incoming values from RHF/MUI into Date | undefined
      const coerceToDate = (value: unknown): Date | undefined => {
        if (value === null || value === undefined || value === '') return undefined

        if (value instanceof Date) {
          return isNaN(value.getTime()) ? undefined : value
        }

        if (typeof value === 'string') {
          const parsed = new Date(value)
          return isNaN(parsed.getTime()) ? undefined : parsed
        }

        return undefined
      }

      let schema: ZodTypeAny = z.preprocess(coerceToDate, z.date({ message: requiredMessage }))

      if (!applyRequiredInBaseSchema) {
        schema = z.preprocess(coerceToDate, z.date()).optional()
      }

      const minAge = v?.minAge
      if (minAge !== undefined) {
        schema = schema.refine(
          (val) => {
            if (!(val instanceof Date)) return !applyRequiredInBaseSchema

            const cutoff = new Date()
            cutoff.setHours(0, 0, 0, 0)
            cutoff.setFullYear(cutoff.getFullYear() - minAge)

            return val.getTime() <= cutoff.getTime()
          },
          {
            message: getValidationMessage(
              'validation.minAge',
              { field: fieldLabel, age: minAge },
              `Devi avere almeno ${minAge} anni`
            )
          }
        )
      }

      if (v?.disabledMonths?.length) {
        schema = schema.refine(
          (val) => {
            if (!(val instanceof Date)) return !applyRequiredInBaseSchema
            return !v.disabledMonths?.includes(val.getMonth())
          },
          {
            message: getValidationMessage(
              'validation.invalidDate',
              { field: fieldLabel },
              `${fieldLabel} non è valida`
            )
          }
        )
      }

      return schema
    }

    case 'hidden': {
      return z.union([z.string(), z.boolean(), z.number(), z.null()]).optional()
    }

    // text, tel, file and others → string
    default: {
      let schema = z.string()
      if (v?.minLength) {
        schema = schema.min(
          v.minLength,
          getValidationMessage(
            'validation.minLength',
            { count: v.minLength },
            `Minimo ${v.minLength} caratteri`
          )
        )
      }
      if (v?.maxLength) {
        schema = schema.max(
          v.maxLength,
          getValidationMessage(
            'validation.maxLength',
            { count: v.maxLength },
            `Massimo ${v.maxLength} caratteri`
          )
        )
      }
      if (v?.pattern)
        schema = schema.regex(
          new RegExp(v.pattern),
          v.patternMessage ??
            getValidationMessage('validation.invalidFormat', undefined, 'Formato non valido')
        )
      if (applyRequiredInBaseSchema && !v?.minLength) {
        schema = schema.min(
          1,
          getValidationMessage(
            'validation.required',
            { field: fieldLabel },
            `${fieldLabel} è obbligatorio`
          )
        )
      }
      if (!applyRequiredInBaseSchema) return schema.optional().or(z.literal(''))
      return schema
    }
  }
}

/**
 * Build a Zod object schema from a FlowStepConfig.
 * Returns a z.ZodObject that can be used with react-hook-form's zodResolver.
 *
 * Only visible fields (based on the current form values) are required;
 * hidden fields get .optional() added via superRefine or are excluded.
 * For simplicity, all fields defined in the config are included in the schema,
 * and visibility is enforced at the UI level.
 */
export function buildStepSchema(step: FlowStepConfig, externalValues?: Record<string, unknown>) {
  const shape: Record<string, ZodTypeAny> = {}

  for (const field of step.fields) {
    shape[field.key] = buildFieldSchema(field)
  }

  return z.object(shape).superRefine((data, ctx) => {
    const stepValues = data as Record<string, unknown>
    // Merge external (cross-step) values so visibility conditions that reference
    // fields from other steps (e.g. tipologia_1, bando_1) are evaluated correctly.
    // Current step values take priority.
    const allValues = { ...externalValues, ...stepValues }

    for (const field of step.fields) {
      if (!field.required) continue

      const hasVisibilityConditions =
        !!field.visibleWhen || !!field.visibleWhenAll?.length || !!field.visibleWhenAny?.length
      if (!hasVisibilityConditions) continue
      if (!isFieldVisible(field, allValues)) continue

      const value = allValues[field.key]
      const fieldLabel = getFieldLabel(field)

      let isValid = true
      let message = getValidationMessage(
        'validation.required',
        { field: fieldLabel },
        `${fieldLabel} è obbligatorio`
      )

      switch (field.type) {
        case 'checkbox':
          isValid = value === true
          message = getValidationMessage(
            'validation.mustBeTrue',
            { field: fieldLabel },
            `${fieldLabel} è obbligatorio`
          )
          break

        case 'select':
        case 'radio':
        case 'city-grid':
          isValid = typeof value === 'string' && value.trim().length > 0
          message = getValidationMessage(
            'validation.selectRequired',
            { field: fieldLabel.toLowerCase() },
            `Seleziona ${fieldLabel.toLowerCase()}`
          )
          break

        case 'date-picker-airbnb':
          isValid = value instanceof Date && !isNaN(value.getTime())
          break

        default:
          isValid = value !== undefined && value !== null && String(value).trim().length > 0
          break
      }

      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field.key],
          message
        })
      }
    }
  })
}

/**
 * Build default values from a FlowStepConfig.
 */
export function buildDefaultValues(
  step: FlowStepConfig,
  savedData?: Record<string, unknown>
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}

  for (const field of step.fields) {
    switch (field.type) {
      case 'checkbox':
        defaults[field.key] = false
        break
      case 'number':
        defaults[field.key] = field.defaultValue ?? undefined
        break
      default:
        defaults[field.key] = field.defaultValue ?? ''
    }
  }

  // Merge with previously saved data
  if (savedData) {
    const dateFields = new Set(
      step.fields.filter((f) => f.type === 'date-picker-airbnb').map((f) => f.key)
    )

    for (const key of Object.keys(defaults)) {
      if (savedData[key] !== undefined) {
        // Restore serialised date strings back to Date objects
        if (dateFields.has(key) && typeof savedData[key] === 'string' && savedData[key]) {
          const parsed = new Date(savedData[key] as string)
          defaults[key] = isNaN(parsed.getTime()) ? null : parsed
        } else {
          defaults[key] = savedData[key]
        }
      }
    }
  }

  return defaults
}
